import '@/components/auth/cosmic.css';
import { CosmicBackground } from '@/components/auth/CosmicBackground';
import { PROVIDERS } from '@/components/auth/providers';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

interface LoginProps {
  /**
   * 'public' (기본): 일반 로그인. 외부 provider 활성 시 SSO 버튼만, 아니면 로컬 폼.
   * 'admin': 관리자 break-glass URL `/login/admin`. 외부 활성 무관 항상 로컬 폼.
   */
  variant?: 'public' | 'admin';
}

type LoginMode = 'sso' | 'form-local' | 'form-ldap' | 'form-admin';

interface ModeConfig {
  eyebrow: string;
  title: string;
  sub: React.ReactNode;
}

export function Login({ variant = 'public' }: LoginProps) {
  const navigate = useNavigate();
  const { login, user, isAuthenticated } = useAuth();
  const branding = useBranding();
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // SSO 콜백 실패(예: ISign+ 미등록 사용자)가 /login?error=... 로 redirect 해 왔을 때 표시.
  const [authErrorCode] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('error'),
  );

  const status = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: api.onboardingStatus,
    refetchOnWindowFocus: false,
  });

  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: api.authProviders,
    refetchOnWindowFocus: false,
  });

  const activeProvider = providers.data?.activeExternalProvider ?? null;

  // mode 결정
  let mode: LoginMode;
  if (variant === 'admin') mode = 'form-admin';
  else if (activeProvider === null) mode = 'form-local';
  else if (activeProvider === 'ldap') mode = 'form-ldap';
  else mode = 'sso';

  const ssoProvider = mode === 'sso' && activeProvider ? PROVIDERS[activeProvider] : null;

  const config = MODE_CONFIG[mode](ssoProvider?.name);
  const authErrorMessage = authErrorCode
    ? (AUTH_ERROR_MESSAGES[authErrorCode] ?? '로그인 중 문제가 발생했습니다. 다시 시도해 주세요.')
    : null;

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    navigate({ to: user.role === 'admin' ? '/admin' : '/chat' });
  }, [isAuthenticated, user, navigate]);

  // sso 모드 + ssoAutoRedirect=true 면 진입 즉시 SSO start endpoint 로 navigate.
  // variant='admin' (break-glass `/login/admin`) 은 mode='form-admin' 이라 여기 안 걸림.
  // 이미 인증된 상태면 위쪽 useEffect 가 먼저 라우팅하므로 SSO 로 다시 안 보냄.
  useEffect(() => {
    if (mode !== 'sso' || !activeProvider) return;
    if (!providers.data?.ssoAutoRedirect) return;
    if (isAuthenticated) return;
    window.location.href = `/api/auth/${activeProvider}/start`;
  }, [mode, activeProvider, providers.data?.ssoAutoRedirect, isAuthenticated]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      // form-ldap 모드: LDAP 전용 endpoint 로 POST (admin/non-admin 분기 영향 없음).
      // form-local / form-admin (break-glass): 로컬 password 경로.
      await login(email, password, mode === 'form-ldap' ? 'ldap' : 'local');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '알 수 없는 오류');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="cosmic-stage" data-theme={theme}>
      <CosmicBackground theme={theme} />

      <header className="head">
        <div className="logo">
          <span className="logo-mark" aria-hidden="true">
            <img
              src={branding.logoUrl}
              alt={branding.serviceName}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </span>
          <span className="logo-name">{branding.serviceName}</span>
        </div>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          aria-label="테마 전환"
        >
          {theme === 'dark' ? (
            <svg
              className="ti"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.6 5.6l1.06 1.06M17.34 17.34l1.06 1.06M5.6 18.4l1.06-1.06M17.34 6.66l1.06-1.06" />
            </svg>
          ) : (
            <svg
              className="ti"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12.8A8 8 0 1 1 11.2 3a6 6 0 0 0 9.8 9.8z" />
            </svg>
          )}
          {theme === 'dark' ? 'Dark' : 'Light'}
        </button>
      </header>

      <main className="card">
        <div className="eyebrow">
          <span className="pulse" />
          {config.eyebrow}
        </div>
        <h1>{config.title}</h1>
        <p className="sub">{config.sub}</p>

        {status.data?.keyMismatch ? (
          <div className="key-mismatch" role="alert">
            ⚠️ INNERMATE_SECRET_KEY 가 일치하지 않습니다. 운영자에게 문의하세요.
          </div>
        ) : null}

        {authErrorMessage ? (
          <div className="key-mismatch" role="alert">
            {authErrorMessage}
          </div>
        ) : null}

        {mode === 'sso' && ssoProvider ? (
          <a className="provider-btn" href={`/api/auth/${activeProvider}/start`}>
            {ssoProvider.icon}
            {ssoProvider.label}
            <svg
              className="arrow"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        ) : (
          <form className="form-stack" onSubmit={submit}>
            <div>
              <label className="field-label" htmlFor="email">
                {mode === 'form-ldap' ? 'LDAP 사용자' : '아이디 / 이메일'}
              </label>
              <input
                id="email"
                type="text"
                autoComplete={mode === 'form-ldap' ? 'username' : 'username'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-field"
                placeholder={mode === 'form-ldap' ? 'user@company.local' : '아이디 또는 이메일'}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="password">
                비밀번호
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-field"
                placeholder="••••••••"
              />
            </div>
            {error ? <div className="field-error">{error}</div> : null}
            <button type="submit" className="submit-btn" disabled={pending}>
              {pending ? (
                <>
                  <span className="spinner" />
                  로그인 중…
                </>
              ) : (
                '로그인'
              )}
            </button>
          </form>
        )}

        <div className="card-foot">
          <svg
            className="lock"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 1 1 8 0v4" />
          </svg>
          {variant === 'admin'
            ? 'Local · Break-glass · Audit logged'
            : mode === 'sso'
              ? 'External Provider · Audit logged'
              : 'Local · Encrypted at rest'}
        </div>
      </main>

      <footer className="bottom-meta">
        <span>© 2026 INNERMATE</span>
        <span>{variant === 'admin' ? 'ADMIN · BREAK-GLASS' : 'AUTHENTICATION'}</span>
      </footer>
    </div>
  );
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  isign_not_registered: '등록되지 않은 사용자입니다. 관리자에게 계정 등록을 문의하세요.',
};

const MODE_CONFIG: Record<LoginMode, (providerName?: string) => ModeConfig> = {
  sso: (providerName) => ({
    eyebrow: 'INNERMATE · SECURE LOGIN',
    title: '다시 오신 걸 환영해요',
    sub: (
      <>
        관리자가 설정한 <strong>{providerName ?? '외부 인증'}</strong> 으로 로그인하세요.
      </>
    ),
  }),
  'form-local': () => ({
    eyebrow: 'INNERMATE · LOCAL ACCESS',
    title: '다시 오신 걸 환영해요',
    sub: '관리자 또는 사용자 계정으로 로그인하세요.',
  }),
  'form-ldap': () => ({
    eyebrow: 'INNERMATE · LDAP DIRECTORY',
    title: '디렉토리 인증',
    sub: (
      <>
        회사 LDAP 디렉토리 계정으로 로그인하세요.
        <br />
        관리자는 별도 경로로 로컬 로그인 가능합니다.
      </>
    ),
  }),
  'form-admin': () => ({
    eyebrow: 'ADMIN · BREAK-GLASS',
    title: '관리자 로그인',
    sub: (
      <>
        외부 인증과 무관하게 항상 동작하는 <strong>로컬</strong> 관리자 로그인입니다.
        <br />
        IP allowlist 가 적용됩니다.
      </>
    ),
  }),
};
